import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminApi,
  type AdminOrder,
  type CustomerSummary,
  type Lead,
} from '../lib/adminApi';

/**
 * Universal admin search. Lives in the sticky top bar above every admin
 * page. Type any of:
 *
 *   • Customer email          (matched against customer + order email)
 *   • Customer name           (matched against displayName)
 *   • Company name or number  (matched against orders)
 *   • Order ID                (e.g. ord_abc123)
 *
 * Debounced ~250ms, runs the order + customer endpoints in parallel,
 * up-to-5 results per group, keyboard navigable.
 */
type Result =
  | { kind: 'order'; id: string; label: string; sub: string }
  | { kind: 'customer'; email: string; label: string; sub: string }
  | { kind: 'lead'; id: string; label: string; sub: string };

export function AdminSearchBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderHits, setOrderHits] = useState<AdminOrder[]>([]);
  const [customerHits, setCustomerHits] = useState<CustomerSummary[]>([]);
  const [leadHits, setLeadHits] = useState<Lead[]>([]);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Flatten + ordered list used by keyboard nav and rendering.
  const results: Result[] = [
    ...leadHits.slice(0, 5).map((l) => ({
      kind: 'lead' as const,
      id: l.id,
      label: `${l.displayId ? l.displayId + ' · ' : ''}${l.name}`,
      sub: `${l.email}${l.businesses?.length ? ` · ${l.businesses.length} business${l.businesses.length === 1 ? '' : 'es'}` : ''}`,
    })),
    ...customerHits.slice(0, 5).map((c) => ({
      kind: 'customer' as const,
      email: c.email,
      label: c.displayName || c.email,
      sub: c.displayName
        ? `${c.email} · ${c.totalFilings} filing${c.totalFilings === 1 ? '' : 's'}`
        : `${c.totalFilings} filing${c.totalFilings === 1 ? '' : 's'}`,
    })),
    ...orderHits.slice(0, 5).map((o) => ({
      kind: 'order' as const,
      id: o.id,
      label: o.companyName,
      sub: `${o.companyNumber} · ${o.email} · ${o.status}`,
    })),
  ];

  // Debounced fetch — runs orders + customers in parallel.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const query = q.trim();
    if (query.length < 2) {
      setOrderHits([]);
      setCustomerHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const [orders, customers, leadsResp] = await Promise.all([
          adminApi.listOrders({ q: query, pageSize: 5 }).catch(() => ({ items: [] })),
          adminApi.listCustomers({ q: query, pageSize: 5 }).catch(() => ({ items: [] })),
          adminApi.listLeads({ q: query }).catch(() => ({ items: [] as Lead[] })),
        ]);
        setOrderHits((orders.items as AdminOrder[]) || []);
        setCustomerHits((customers.items as CustomerSummary[]) || []);
        setLeadHits(((leadsResp as { items?: Lead[] }).items || []).slice(0, 5));
        setHighlight(0);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q]);

  // Close on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Cmd/Ctrl-K to focus the search bar from anywhere in admin.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function go(r: Result) {
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
    if (r.kind === 'customer') {
      navigate(`/admin/customers/${encodeURIComponent(r.email)}`);
    } else if (r.kind === 'lead') {
      // No dedicated per-lead route yet — send the operator to the
      // leads list, which will already be filtered by the ID they
      // searched for since the query is preserved in the input.
      navigate(`/admin/leads`);
    } else {
      navigate(`/admin/orders/${encodeURIComponent(r.id)}`);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || results.length === 0) {
      if (e.key === 'Enter' && q.trim().startsWith('ord_')) {
        navigate(`/admin/orders/${encodeURIComponent(q.trim())}`);
        setQ('');
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[highlight];
      if (r) go(r);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder="Search customers, companies, orders…"
        autoComplete="off"
        spellCheck={false}
        className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 pl-9 pr-10 text-sm focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      {/* search icon */}
      <svg
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      {loading && (
        <span className="absolute right-10 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
          …
        </span>
      )}
      {/* ⌘K hint */}
      <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 select-none rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400 sm:inline-block">
        ⌘K
      </kbd>

      {open && (results.length > 0 || (q.trim().length >= 2 && !loading)) && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-xs text-slate-500">
              No matches for "{q}". Try an email, company name or order ID.
            </div>
          ) : (
            <>
              {leadHits.length > 0 && <GroupHeader>Leads</GroupHeader>}
              {results
                .filter((r) => r.kind === 'lead')
                .map((r) => (
                  <ResultRow
                    key={`l-${(r as { id: string }).id}`}
                    r={r}
                    active={results.indexOf(r) === highlight}
                    onMouseEnter={() => setHighlight(results.indexOf(r))}
                    onClick={() => go(r)}
                  />
                ))}
              {customerHits.length > 0 && (
                <GroupHeader>Customers</GroupHeader>
              )}
              {results
                .filter((r) => r.kind === 'customer')
                .map((r, i) => (
                  <ResultRow
                    key={`c-${(r as { email: string }).email}`}
                    r={r}
                    active={results.indexOf(r) === highlight}
                    onMouseEnter={() => setHighlight(results.indexOf(r))}
                    onClick={() => go(r)}
                    index={i}
                  />
                ))}
              {orderHits.length > 0 && <GroupHeader>Orders</GroupHeader>}
              {results
                .filter((r) => r.kind === 'order')
                .map((r) => (
                  <ResultRow
                    key={`o-${(r as { id: string }).id}`}
                    r={r}
                    active={results.indexOf(r) === highlight}
                    onMouseEnter={() => setHighlight(results.indexOf(r))}
                    onClick={() => go(r)}
                  />
                ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function GroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t border-slate-100 bg-slate-50 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 first:border-t-0">
      {children}
    </div>
  );
}

function ResultRow({
  r,
  active,
  onMouseEnter,
  onClick,
}: {
  r: Result;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  index?: number;
}) {
  return (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onMouseDown={(e) => {
        e.preventDefault(); // keep focus
        onClick();
      }}
      className={`flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors ${
        active ? 'bg-brand-50' : 'bg-white hover:bg-slate-50'
      }`}
    >
      <div
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs ${
          r.kind === 'customer'
            ? 'bg-brand-100 text-brand-700'
            : r.kind === 'lead'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-slate-100 text-slate-700'
        }`}
      >
        {r.kind === 'customer' ? '👤' : r.kind === 'lead' ? '🔥' : '📄'}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-900">{r.label}</div>
        <div className="truncate text-xs text-slate-500">{r.sub}</div>
      </div>
    </button>
  );
}
